import React, { useEffect, useState } from "react";
import { Box, Flex } from "theme-ui";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import BasicButton from "components/forms/BasicButton";
import ProjectFileDrop from "components/ProjectFileDrop";
import NeededByField from "components/NeededByField";
import { getEarlyPayOffers, getProjectFiles, EarlyPayOffer } from "api/Projects";
import { themevals } from "theme/themevals";
import { PaymentsIcon, FilesIcon, CalendarIcon } from "components/svg";

/**
 * NEXT STEPS ON THE CLIENT'S PROJECT (2026-10-12, Jack: "a little
 * disorganized" - the PO nudge sat full-width above the tabs and the
 * early-pay banner sat below it in Overview, two separate asks).
 *
 * One card, top of Overview, one line per open item, each with its own
 * action. Done items drop off; when nothing is open the card is gone.
 *   1. Pay early and save   (open early-pay offer)        -> Pay now
 *   2. Add PO / site maps   (no files yet, before invoice) -> opens upload in place
 *   3. Date needed by       (not given yet, before Complete) -> date field in place
 * Changing a given date lives in the sidebar (Date Needed By).
 *
 * Color is for meaning, not decoration (Jack asked about more color): a
 * tinted icon per step, the money row in green with the saving
 * highlighted, and the days-left pill turning amber in the last 2 days.
 */
const money = (n: number) => Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const ProjectNextSteps: React.FC<{ project: any }> = ({ project }) => {
  const navigate = useNavigate();
  const [offer, setOffer] = useState<EarlyPayOffer | null>(null);
  const [fileCount, setFileCount] = useState<number | null>(null);
  const [filesOpen, setFilesOpen] = useState(false);
  const [dateDone, setDateDone] = useState(false);

  const step = Number(project?.status?.stepNumber || 0);
  const statusName = String(project?.status?.name || "").toLowerCase();
  const closed = ["closed", "cancelled", "canceled"].includes(statusName);

  useEffect(() => {
    if (!project?.id) return;
    let live = true;
    void getEarlyPayOffers().then((all) => {
      if (!live) return;
      setOffer(all.find((o) => o.projectId === project.id && moment(o.deadline).isAfter(moment())) ?? null);
    });
    void getProjectFiles({ data: { id: project.id } }).then((res: any) => {
      if (live) setFileCount(Array.isArray(res) ? res.length : 0);
    });
    return () => {
      live = false;
    };
  }, [project?.id]);

  const showFiles = !closed && (!step || step < 6) && (fileCount === 0 || filesOpen);
  const showDate = !closed && (!step || step < 5) && !project?.clientNeededBy && !dateDone;
  const rows = [offer ? "pay" : null, showFiles ? "files" : null, showDate ? "date" : null].filter(Boolean) as string[];
  if (!rows.length) return null;

  const end = offer ? moment(offer.deadline) : null;
  const daysLeft = end ? Math.max(Math.ceil(end.diff(moment(), "hours") / 24), 0) : 0;

  // Icon tints: green = money / savings, neutral grey for paperwork.
  const TINT: Record<string, { bg: string; fg: string }> = {
    pay: { bg: "#E6F2DA", fg: "#3B6D11" },
    files: { bg: "#EEF0F2", fg: "#4A5560" },
    date: { bg: "#EEF0F2", fg: "#4A5560" },
  };
  const ICON: Record<string, React.ReactNode> = {
    pay: <PaymentsIcon fill={TINT.pay.fg} width="16px" height="16px" />,
    files: <FilesIcon fill={TINT.files.fg} width="16px" height="16px" />,
    date: <CalendarIcon fill={TINT.date.fg} width="16px" height="16px" />,
  };
  const row = (key: string, title: React.ReactNode, sub: React.ReactNode, action: React.ReactNode, body?: React.ReactNode) => (
    <Box key={key} sx={{ padding: "12px 16px", borderTop: "1px solid " + themevals.colors.inputOutline }}>
      <Flex sx={{ alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
        <Flex sx={{ minWidth: 0, flex: "1 1 260px", gap: "12px", alignItems: "center" }}>
          <Flex sx={{ width: "32px", height: "32px", flexShrink: 0, borderRadius: "50%", backgroundColor: TINT[key].bg, alignItems: "center", justifyContent: "center" }}>
            {ICON[key]}
          </Flex>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: "14px", fontWeight: 600, color: "#111" }}>{title}</Box>
            <Box sx={{ fontSize: "12px", color: themevals.colors.dkgrey, lineHeight: 1.5 }}>{sub}</Box>
          </Box>
        </Flex>
        {action}
      </Flex>
      {body ? <Box sx={{ paddingLeft: "44px" }}>{body}</Box> : null}
    </Box>
  );
  const urgent = daysLeft <= 2;
  const pill = (
    <Box
      as="span"
      sx={{
        display: "inline-block", fontSize: "11px", fontWeight: 600, padding: "1px 8px", borderRadius: "999px", marginRight: "6px",
        backgroundColor: urgent ? "#FDF0DA" : "#E6F2DA", color: urgent ? "#8A5300" : "#3B6D11",
      }}
    >
      {daysLeft <= 1 ? "Last day" : `${daysLeft} days left`}
    </Box>
  );

  return (
    <Box sx={{ border: "1px solid " + themevals.colors.inputOutline, borderRadius: "8px", backgroundColor: "#fff", marginBottom: "20px", fontFamily: "Poppins, sans-serif" }}>
      <Flex sx={{ padding: "12px 16px", alignItems: "baseline", gap: "8px" }}>
        <Box sx={{ fontSize: "14px", fontWeight: 600, color: "#111" }}>Next steps</Box>
        <Box sx={{ fontSize: "12px", color: themevals.colors.dkgrey }}>{`${rows.length} to do`}</Box>
      </Flex>

      {offer &&
        end &&
        row(
          "pay",
          <>
            {`Pay by ${end.format("ddd, MMM D")} to `}
            <Box as="span" sx={{ color: "#3B6D11" }}>{`save up to ${money(offer.fullSave)}`}</Box>
          </>,
          <>
            {pill}
            {`${money(offer.fullAmount)} in full, or ${money(offer.depositAmount)} now as a 50% deposit`}
          </>,
          <BasicButton type="button" scheme="primary" keepText={true} onClick={() => navigate(`/invoices/${offer.invoiceId}?pay=1`)}>
            Pay now
          </BasicButton>,
        )}

      {showFiles &&
        row(
          "files",
          "Add your PO, site maps or site photos",
          "Your PO lets us bill it right, site maps show where to install, and current photos confirm the site is ready.",
          !filesOpen ? (
            <BasicButton type="button" scheme="secondary" outline={true} keepText={true} onClick={() => setFilesOpen(true)}>
              Add files
            </BasicButton>
          ) : null,
          filesOpen ? (
            <Box sx={{ marginTop: "10px" }}>
              <ProjectFileDrop projectId={project.id} variant="embedded" clientPO={project.clientPO} onUploaded={() => setFileCount((n) => (n ?? 0) + 1)} />
            </Box>
          ) : null,
        )}

      {showDate &&
        row(
          "date",
          "When do you need this done?",
          "If your PO or job has a deadline, tell us here. We'll confirm when we schedule.",
          <NeededByField projectId={project.id} rfqDate={project.originQuoteRequest?.serviceDate} onSaved={() => setDateDone(true)} />,
        )}
    </Box>
  );
};

export default ProjectNextSteps;
